/** Daily quiz activity for a GitHub-style year chart, keyed by chess mode. */

import type { ModeId } from '../modes/modes';

export interface DayActivity {
  correct: number;
  total: number;
}

export type ActivityMap = Record<string, DayActivity>;

const LEGACY_OPENING_KEY = 'octodojo.quiz-activity.v1';

export function activityStorageKey(mode: ModeId): string {
  return `octodojo.activity.${mode}.v1`;
}

export function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function addDays(d: Date, n: number): Date {
  const next = new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
  return startOfLocalDay(next);
}

export function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseActivityMap(raw: string | null): ActivityMap {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: ActivityMap = {};
    for (const [key, val] of Object.entries(parsed)) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) continue;
      if (!val || typeof val !== 'object') continue;
      const rec = val as { correct?: unknown; total?: unknown };
      const correct = Number(rec.correct);
      const total = Number(rec.total);
      if (!Number.isFinite(correct) || !Number.isFinite(total) || total < 0) continue;
      out[key] = { correct: Math.max(0, correct), total };
    }
    return out;
  } catch {
    return {};
  }
}

export function loadActivity(mode: ModeId = 'opening'): ActivityMap {
  if (typeof localStorage === 'undefined') return {};
  const primary = parseActivityMap(localStorage.getItem(activityStorageKey(mode)));
  if (Object.keys(primary).length > 0) return primary;
  if (mode === 'opening') {
    const legacy = parseActivityMap(localStorage.getItem(LEGACY_OPENING_KEY));
    if (Object.keys(legacy).length > 0) {
      saveActivity(legacy, mode);
      return legacy;
    }
  }
  return {};
}

export function saveActivity(map: ActivityMap, mode: ModeId = 'opening'): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(activityStorageKey(mode), JSON.stringify(map));
  } catch {
    /* quota / private mode */
  }
}

export function formatDayScore(day: DayActivity | undefined): string {
  const wins = day?.correct ?? 0;
  const tries = day?.total ?? 0;
  const winWord = wins === 1 ? 'win' : 'wins';
  const tryWord = tries === 1 ? 'try' : 'tries';
  return `${wins} ${winWord} of ${tries} ${tryWord}`;
}

export function recordAttempt(
  map: ActivityMap,
  matched: boolean,
  now: Date = new Date(),
): ActivityMap {
  const key = dayKey(now);
  const prev = map[key] ?? { correct: 0, total: 0 };
  return {
    ...map,
    [key]: {
      correct: prev.correct + (matched ? 1 : 0),
      total: prev.total + 1,
    },
  };
}

/** Sunday-start weeks covering the last `weekCount` weeks through today. */
export function yearWeeks(now: Date = new Date(), weekCount = 53): Date[][] {
  const today = startOfLocalDay(now);
  const sunday = addDays(today, -today.getDay());
  const first = addDays(sunday, -(weekCount - 1) * 7);
  const weeks: Date[][] = [];
  for (let w = 0; w < weekCount; w++) {
    const days: Date[] = [];
    for (let d = 0; d < 7; d++) days.push(addDays(first, w * 7 + d));
    weeks.push(days);
  }
  return weeks;
}

/** Play count that reaches max brightness. */
export const BRIGHT_AT = 9;

/**
 * Empty = dark. Hue red→green by accuracy. Lightness up with play count
 * (full brightness at `BRIGHT_AT` plays).
 */
export function activityFill(day: DayActivity | undefined): string {
  if (!day || day.total <= 0) return 'var(--activity-empty)';
  const pct = Math.min(1, Math.max(0, day.correct / day.total));
  const hue = Math.round(pct * 120);
  const t = Math.min(1, day.total / BRIGHT_AT);
  const sat = 62 + t * 18;
  const light = 18 + t * 34;
  return `hsl(${hue} ${sat}% ${light}%)`;
}
